/* ============================================================================
 * slug.js - URL slugs, route parsing, and document <head> for SEO
 * ----------------------------------------------------------------------------
 * URL scheme (relative to the app base, e.g. "/meridian/"):
 *   /                         → home / overview
 *   /rankings                 → rankings
 *   /watchlist                → watchlist
 *   /:county                  → county page      e.g. /mclennan
 *   /:county/:city            → city page        e.g. /mclennan/waco
 *   /:county/:city/:bar       → business profile e.g. /mclennan/waco/georges
 * ==========================================================================*/

export const SITE_NAME = "Texas Bar Sales";

/** Vite injects the configured base ("/meridian/" in prod, "/" in dev). */
export const BASE = (import.meta.env?.BASE_URL || "/").replace(/\/*$/, "/");

/** URL-safe slug: lowercase, ASCII, hyphen-separated. Stable + reversible-ish. */
export function slugify(s) {
  return (
    String(s || "")
      .toLowerCase()
      .normalize("NFKD").replace(/[̀-ͯ]/g, "") // strip accents
      .replace(/['’.]/g, "")                              // drop apostrophes/dots (george's → georges)
      .replace(/[^a-z0-9]+/g, "-")                        // everything else → hyphen
      .replace(/^-+|-+$/g, "")                            // trim hyphens
    || "-"
  );
}

/** Reverse a slug back to a query string ("san-antonio" → "san antonio").
 * The data layer matches city on upper(location_city), so spacing is enough. */
export const deslug = (s) => String(s || "").replace(/-/g, " ").trim();

const RESERVED = new Set(["rankings", "explore", "about", "map", "cities", "pricing", "welcome", "watchlist", "watch", "signin", "account", "admin"]);

/** Parse a pathname into a route descriptor. */
export function parseRoute(pathname = window.location.pathname) {
  let p = pathname;
  if (p.startsWith(BASE)) p = p.slice(BASE.length);
  p = decodeURIComponent(p).replace(/^\/+|\/+$/g, "");
  const seg = p ? p.split("/") : [];
  if (seg.length === 0) return { view: "overview" };
  if (seg[0] === "rankings") return { view: "rankings" };
  if (seg[0] === "explore") return { view: "explore" };
  if (seg[0] === "about") return { view: "about" };
  if (seg[0] === "map") return { view: "map" };
  if (seg[0] === "cities") return { view: "cities" };
  if (seg[0] === "pricing") return { view: "pricing" };
  if (seg[0] === "welcome") return { view: "welcome" };
  if (seg[0] === "signin") return { view: "signin" };
  if (seg[0] === "account") return { view: "account" };
  if (seg[0] === "admin") return { view: "admin" };
  if (seg[0] === "watchlist" || seg[0] === "watch") return { view: "watchlist" };
  if (RESERVED.has(seg[0]) && seg.length === 1) return { view: "overview" };
  if (seg.length === 1) return { view: "county", county: seg[0] };
  if (seg.length === 2) return { view: "city", county: seg[0], city: seg[1] };
  return { view: "business", county: seg[0], city: seg[1], bar: seg.slice(2).join("-") };
}

/* -------------------------------------------------------------- path builders */
export const homePath = () => BASE;
export const rankingsPath = () => `${BASE}rankings`;
export const explorePath = () => `${BASE}explore`;
export const aboutPath = () => `${BASE}about`;
export const mapPath = () => `${BASE}map`;
export const citiesPath = () => `${BASE}cities`;
export const pricingPath = () => `${BASE}pricing`;
export const signinPath = () => `${BASE}signin`;
export const accountPath = () => `${BASE}account`;
export const adminPath = () => `${BASE}admin`;
export const watchlistPath = () => `${BASE}watchlist`;
export const countyPath = (county) => `${BASE}${slugify(county)}`;
export const cityPath = (county, city) => `${BASE}${slugify(county)}/${slugify(city)}`;
export const bizPath = (b) => `${BASE}${slugify(b.county)}/${slugify(b.city)}/${slugify(b.name)}`;

/* ------------------------------------------------------------- document <head> */
function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

/** Absolute path to the branded social card (served from the site root). */
export const OG_IMAGE_PATH = `${BASE}og-image.png`;

function upsertJsonLd(json) {
  let el = document.getElementById("ld-json");
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "ld-json"; document.head.appendChild(el); }
  el.textContent = JSON.stringify(json);
}

/** Set title, description, canonical, Open Graph/Twitter tags, and JSON-LD for
 * a view. Client-side (Google renders JS); build-time prerender injects the
 * same tags into static HTML so non-JS crawlers see them too. */
export function setHead({ title, description, path, index = true, view, data }) {
  document.title = title;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const p = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const url = origin + p;
  const ogImg = origin + OG_IMAGE_PATH;
  upsertMeta("name", "description", description);
  upsertMeta("name", "robots", index ? "index,follow" : "noindex,follow");
  upsertLink("canonical", url);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:image", ogImg);
  upsertMeta("property", "og:image:width", "1200");
  upsertMeta("property", "og:image:height", "630");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", ogImg);
  if (view) upsertJsonLd(jsonLd(view, { ...data, path: p }, origin));
}

/* ------------------------------------------------------------- structured data
 * schema.org JSON-LD per view. Pure (origin passed in) so the build-time
 * prerender can inject the identical markup into static HTML. */
export function jsonLd(view, d = {}, origin = "") {
  const abs = (path) => origin + path;
  const home = abs(BASE);
  const crumbs = (parts) => ({
    "@type": "BreadcrumbList",
    itemListElement: parts.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.name,
      ...(it.path ? { item: abs(it.path) } : {}),
    })),
  });
  const org = { "@type": "Organization", "@id": home + "#org", name: SITE_NAME, url: home, logo: abs(OG_IMAGE_PATH) };
  const website = {
    "@type": "WebSite", "@id": home + "#website", name: SITE_NAME, url: home, publisher: { "@id": home + "#org" },
    potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: `${home}?q={search_term_string}` }, "query-input": "required name=search_term_string" },
  };
  let graph;
  if (view === "county") {
    graph = [crumbs([{ name: "Texas Bar Sales", path: BASE }, { name: `${d.county} County` }]),
      { "@type": ["Place", "AdministrativeArea"], name: `${d.county} County, Texas`, url: abs(d.path || BASE) }];
  } else if (view === "city") {
    graph = [crumbs([{ name: "Texas Bar Sales", path: BASE }, ...(d.county ? [{ name: `${d.county} County`, path: countyPath(d.county) }] : []), { name: d.city }]),
      { "@type": ["Place", "City"], name: `${d.city}, Texas`, url: abs(d.path || BASE) }];
  } else if (view === "business") {
    graph = [crumbs([{ name: "Texas Bar Sales", path: BASE },
      ...(d.county ? [{ name: `${d.county} County`, path: countyPath(d.county) }] : []),
      ...(d.city && d.county ? [{ name: d.city, path: cityPath(d.county, d.city) }] : []),
      { name: d.name }]),
      { "@type": ["LocalBusiness", "BarOrPub"], name: d.name, url: abs(d.path || BASE),
        address: { "@type": "PostalAddress", ...(d.city ? { addressLocality: d.city } : {}), addressRegion: "TX", addressCountry: "US" },
        parentOrganization: { "@id": home + "#org" } }];
  } else {
    graph = [website, org,
      { "@type": "Dataset", name: "Texas Mixed-Beverage Gross Receipts", url: home,
        description: "Monthly reported mixed-beverage (bar) sales for every tracked venue, city, and county in Texas.",
        creator: { "@id": home + "#org" }, isAccessibleForFree: true,
        spatialCoverage: "Texas, USA", license: "https://data.texas.gov/",
        sourceOrganization: [{ "@type": "GovernmentOrganization", name: "Texas Comptroller of Public Accounts" }, { "@type": "GovernmentOrganization", name: "Texas Alcoholic Beverage Commission" }] }];
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

/** Per-view title + description. `d` carries any names needed. */
export function headFor(view, d = {}) {
  switch (view) {
    case "rankings":
      return { title: `Texas Bar Sales Rankings · Top Venues & Markets | ${SITE_NAME}`,
        description: "Leaderboards of the top-grossing bars, fastest-growing venues, and leading markets across Texas, by reported mixed-beverage sales." };
    case "explore":
      return { title: `Explore Texas Bar Sales · Search & Filter Every Venue | ${SITE_NAME}`,
        description: "Filter and search every tracked bar, restaurant, club and hotel in Texas by city, county, category, and sales range." };
    case "about":
      return { title: `About & Methodology | ${SITE_NAME}`,
        description: "How Texas Bar Sales works: data sourced from the Texas Comptroller mixed-beverage gross receipts dataset and TABC permits, our methodology, and monthly update cadence." };
    case "map":
      return { title: `Texas Bar Sales Map · Sales by County | ${SITE_NAME}`,
        description: "Interactive map of reported mixed-beverage (bar) sales across all 254 Texas counties. Spot hotspots, compare markets, and click any county to drill into its venues." };
    case "cities":
      return { title: `Texas Cities by Bar Sales · Top Markets | ${SITE_NAME}`,
        description: "Every major Texas city ranked by reported mixed-beverage (bar) sales: total revenue, tracked venues, and monthly trends. Click a city for its full market report." };
    case "pricing":
      return { title: `Pricing · Free & Pro Plans | ${SITE_NAME}`,
        description: "Texas Bar Sales is free to explore. Pro adds CSV export, full multi-year sales history for every venue, and unlimited watchlists." };
    case "welcome":
      return { title: `Welcome to Pro | ${SITE_NAME}`,
        description: "Your Texas Bar Sales Pro subscription is active.", index: false };
    case "signin":
      return { title: `Sign in | ${SITE_NAME}`, description: "Sign in or create a free Texas Bar Sales account.", index: false };
    case "account":
      return { title: `Your account | ${SITE_NAME}`, description: "Manage your Texas Bar Sales plan and billing.", index: false };
    case "admin":
      return { title: `Admin | ${SITE_NAME}`, description: "Subscription administration.", index: false };
    case "watchlist":
      return { title: `Your Watchlist | ${SITE_NAME}`,
        description: "Businesses you follow, tracked month over month.", index: false };
    case "county":
      return { title: `Bar Sales in ${d.county} County | ${SITE_NAME}`,
        description: `Reported mixed-beverage (bar) sales across ${d.county} County, Texas: top venues and cities, updated monthly from Texas Comptroller filings.` };
    case "city":
      return { title: `Bar Sales in ${d.city} | ${SITE_NAME}`,
        description: `Reported bar and mixed-beverage sales for ${d.city}, Texas: ranked venues, revenue mix, and monthly trend.` };
    case "business":
      return { title: `${d.name} Bar Sales | ${SITE_NAME}`,
        description: `${d.name}${d.city ? ` in ${d.city}, ${d.county} County` : ""} · reported mixed-beverage sales, 12-month trend, revenue mix, and market position.` };
    default:
      return { title: `${SITE_NAME} · Mixed-Beverage Sales by Bar, City & County in Texas`,
        description: "Search and analyze reported mixed-beverage (bar) sales for every tracked venue, city, and county in Texas. Updated monthly from Texas Comptroller data." };
  }
}
